import React from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { Pill } from 'ui-components/components/pill';
import { Button } from 'ui-components/components/button';
import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';

import { FlexRow } from 'ui-components/components/flex';
import { InfoIcon } from 'ui-components/components/info-icon';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { CellPropTypes } from '../../../../prop-types/react-table';
import { DomainStatus } from '../domain-status';
import { messages } from './messages';

import style from './table-cells.module.less';

const statusMap = {
  [DomainStatus.Unsupported]: {
    messageDescriptor: messages.unsupported,
    className: style['error-pill'],
  },
  [DomainStatus.Pending]: {
    messageDescriptor: messages.pending,
    className: style['neutral-pill'],
  },
  [DomainStatus.Authenticated]: {
    messageDescriptor: messages.authenticated,
    className: style['success-pill'],
  },
  [DomainStatus.Unauthenticated]: {
    messageDescriptor: messages.unauthenticated,
    className: style['error-pill'],
  },
};

export const DomainStatusCell = ({ value, row, onAuthenticateDomain }) => {
  const intl = useIntl();
  const originalRow = row?.original || {};

  // If the domain status is undefined, then the domain has not been created
  if (!value) {
    return (
      <Button
        className={style['table-cell-button']}
        disabled={!originalRow.emailVerification}
        onClick={() => onAuthenticateDomain(originalRow.storeId)}
        theme={THEME_CONSTANTS.tertiary}
      >
        <FormattedMessage {...messages.authenticateDomainLabel} />
      </Button>
    );
  }

  const status = statusMap[value];
  if (status) {
    return <Pill className={status.className} label={intl.formatMessage(status.messageDescriptor)} />;
  }
};

DomainStatusCell.propTypes = {
  ...CellPropTypes,
  onAuthenticateDomain: PropTypes.func.isRequired,
};

const DomainStatusHeader = () => {
  return (
    <FlexRow className={style['header-with-tooltip']}>
      <FormattedMessage {...messages.domainStatusColumnHeader} />
      <InfoIcon
        className={style['info-icon']}
        color={THEME_CONSTANTS.unstyled}
        size='small'
        tooltipText={<FormattedMessage {...messages.domainAuthenticationHeaderTooltip} />}
      />
    </FlexRow>
  );
};

export const createDomainStatusColumnDefinition = (onAuthenticateDomain, columnOverrides) =>
  createColumnDefinitionBuilder({
    accessor: 'domainVerificationStatus',
    Header: DomainStatusHeader,
    Cell: (props) => <DomainStatusCell {...props} onAuthenticateDomain={onAuthenticateDomain} />,
    width: 2,
  })(columnOverrides);
